import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Check, 
  X,
  ArrowRight, 
  Car,
  Activity,
  DollarSign,
  TrendingUp,
  Users,
  FileText,
  BarChart3,
  Clock,
  Zap,
  ChevronDown,
  ChevronUp,
  Building2,
  Mail,
  Phone,
  MapPin,
  Loader2,
  Lock,
  Eye,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Slider } from '../ui/slider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ HERO SECTION ============
const HeroSection = () => {
  const benefits = [
    'Up to $1,000,000 Transportation Coverage',
    'No Large Annual Premium Deposits',
    'Expand Your Fleet Without Massive Insurance Increases',
    'Coverage Activated During Eligible Passenger Transport',
    'Designed Specifically For NEMT Operations'
  ];

  return (
    <section className="relative min-h-screen flex items-center py-20 overflow-hidden bg-slate-950">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,212,255,0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-8">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-400 font-medium">Medical Transportation Insurance Reimagined</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The First <span className="text-cyan-400">Pay-Per-Mile</span> Insurance Program
            <br />Built Exclusively for NEMT Providers
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-12">
            Stop paying high annual insurance premiums regardless of utilization. 
            Medex Insurance Group aligns insurance costs with actual transportation activity.
          </p>

          {/* Key Benefits */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/50 border border-slate-800"
              >
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-cyan-400" />
                </div>
                <span className="text-white text-sm text-left">{benefit}</span>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => document.getElementById('apply-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-400 text-slate-900 font-bold text-lg rounded-xl hover:shadow-[0_0_40px_rgba(0,212,255,0.3)] transition-all inline-flex items-center gap-2"
            data-testid="hero-cta"
          >
            Become a Founding Provider
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============ SECTION 1: WHY TRADITIONAL INSURANCE IS BROKEN ============
const WhyBrokenSection = () => {
  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Traditional NEMT Insurance Is <span className="text-red-400">Broken</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Traditional Insurance */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-red-500/5 border border-red-500/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Traditional Insurance</h3>
            </div>

            <p className="text-slate-400 mb-6">You pay:</p>
            <ul className="space-y-3 mb-8">
              {[
                'Whether vehicles move or not',
                'Whether trips are completed or not',
                'Whether revenue is generated or not'
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300">
                  <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-slate-400 mb-4">Problems:</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                'High annual premiums',
                'Large down payments',
                'Difficult fleet expansion',
                'Limited flexibility'
              ].map((problem, i) => (
                <div key={i} className="p-3 rounded-lg bg-red-500/10 text-red-300 text-sm text-center">
                  {problem}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Medex Insurance Group */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-cyan-500/5 border border-cyan-500/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Medex Insurance Group</h3>
            </div>

            <p className="text-cyan-400 font-medium text-lg mb-4">
              You pay based on transportation activity.
            </p>
            <p className="text-slate-400 mb-8">
              Insurance costs scale with revenue-generating operations.
            </p>

            <div className="p-6 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <p className="text-white text-center">
                This creates a more <span className="text-cyan-400 font-bold">predictable</span> and{' '}
                <span className="text-cyan-400 font-bold">growth-friendly</span> model for transportation providers.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============ SECTION 2: HOW THE PROGRAM WORKS ============
const HowItWorksSection = () => {
  const steps = [
    {
      number: 1,
      title: 'Maintain State Required Auto Insurance',
      description: 'All participating providers must maintain legally required automobile insurance coverage for their vehicles.',
      icon: Shield
    },
    {
      number: 2,
      title: 'Enroll In Medex Insurance Group',
      description: 'Drivers and vehicles are verified through the Medex platform.',
      icon: Users
    },
    {
      number: 3,
      title: 'Transportation Coverage Activates',
      description: 'During approved transportation activity, Medex provides additional transportation coverage protection.',
      icon: Zap
    },
    {
      number: 4,
      title: 'Mileage Is Automatically Tracked',
      description: 'Mileage reporting is integrated through approved dispatch and trip management systems.',
      icon: Activity
    },
    {
      number: 5,
      title: 'Pay Based On Actual Activity',
      description: 'Insurance expenses align with transportation operations rather than fixed annual premiums.',
      icon: TrendingUp
    }
  ];

  return (
    <section className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How The Program <span className="text-cyan-400">Works</span>
          </h2>
        </motion.div>

        <div className="space-y-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-6 p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-cyan-500/30 transition-colors"
            >
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center border border-cyan-500/30">
                  <span className="text-2xl font-bold text-cyan-400">{step.number}</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ SECTION 3: WHY THIS MODEL WORKS ============
const WhyModelWorksSection = () => {
  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why This Model <span className="text-cyan-400">Works</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Traditional Example */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700"
          >
            <h3 className="text-xl font-bold text-slate-400 mb-6">Traditional Insurance Example</h3>
            <p className="text-lg text-white mb-4">5 Vehicle Fleet</p>
            
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-slate-400 text-sm">Annual Premium</p>
                <p className="text-3xl font-bold text-red-400">$60,000</p>
              </div>
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-slate-400 text-sm">Required Down Payment</p>
                <p className="text-3xl font-bold text-red-400">$15,000+</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-700/50 text-center">
                <p className="text-slate-400 text-sm">
                  Coverage Cost Exists Even When Vehicles Are Idle
                </p>
              </div>
            </div>
          </motion.div>

          {/* Medex Example */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/30"
          >
            <h3 className="text-xl font-bold text-cyan-400 mb-6">Medex Insurance Group Example</h3>
            <p className="text-lg text-white mb-4">5 Vehicle Fleet</p>
            
            <div className="space-y-4">
              {[
                'No Large Upfront Premium',
                'Costs Scale With Actual Transportation Activity',
                'Easier Fleet Growth',
                'Better Cash Flow Management'
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-white">{benefit}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Custom Pricing Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 rounded-2xl bg-slate-800/80 border border-slate-700 text-center"
        >
          <h3 className="text-xl font-bold text-white mb-4">Custom Transportation Risk Pricing</h3>
          <p className="text-slate-400 mb-6">Rates are determined by:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['State', 'Fleet Size', 'Claims History', 'Vehicle Types', 'Transportation Volume', 'Safety Performance'].map((factor, i) => (
              <span key={i} className="px-4 py-2 rounded-full bg-slate-700 text-slate-300 text-sm">
                {factor}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============ SECTION 4: WHAT MAKES MEDEX DIFFERENT ============
const WhatMakesDifferentSection = () => {
  const dataPoints = [
    'Driver Activity',
    'Vehicle Utilization',
    'Compliance Status',
    'Trip Completion',
    'Safety Data',
    'Transportation Trends'
  ];

  return (
    <section className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Makes Medex <span className="text-cyan-400">Different</span>
            </h2>
            
            <p className="text-lg text-slate-400 mb-6">
              Most insurance companies only see <span className="text-red-400 font-medium">claims</span>.
            </p>
            
            <p className="text-lg text-white mb-8">
              Medex sees:
            </p>

            <div className="grid grid-cols-2 gap-3">
              {dataPoints.map((point, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20"
                >
                  <Eye className="w-4 h-4 text-cyan-400" />
                  <span className="text-white text-sm">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/30"
          >
            <p className="text-xl text-white leading-relaxed">
              Because Medex operates within the NEMT industry, we can create insurance solutions{' '}
              <span className="text-cyan-400 font-bold">designed specifically</span> for transportation providers.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============ SECTION 5: COVERAGE BENEFITS ============
const CoverageBenefitsSection = () => {
  const benefits = [
    {
      icon: Shield,
      title: 'Transportation Coverage',
      description: 'Additional transportation liability protection during eligible passenger transport.'
    },
    {
      icon: TrendingUp,
      title: 'Fleet Growth Friendly',
      description: 'Add vehicles without facing large annual premium increases.'
    },
    {
      icon: Users,
      title: 'Driver Monitoring',
      description: 'Integrated compliance and credential verification.'
    },
    {
      icon: FileText,
      title: 'Claims Management',
      description: 'Streamlined reporting and documentation processes.'
    },
    {
      icon: BarChart3,
      title: 'Risk Analytics',
      description: 'Visibility into operational risk factors.'
    }
  ];

  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Coverage <span className="text-cyan-400">Benefits</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-cyan-500/30 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
              <p className="text-slate-400 text-sm">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ SECTION 6: FOUNDING PROVIDER PROGRAM ============
const FoundingProviderSection = () => {
  const benefits = [
    'Priority Enrollment',
    'Reduced Startup Costs',
    'Locked Founding Status',
    'Early Access To New Features',
    'Dedicated Support'
  ];

  return (
    <section className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Founding Provider <span className="text-cyan-400">Program</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Launching State By State
          </p>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
            To ensure stable pricing and strong program reserves, Medex Insurance Group launches coverage in phases.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto p-8 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/30"
        >
          <h3 className="text-xl font-bold text-white mb-6 text-center">Founding Provider Benefits</h3>
          <div className="space-y-4">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/50 border border-slate-700">
                <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-white">{benefit}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-amber-400 mt-6 text-sm font-medium">
            Limited provider positions available in each market.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ============ SECTION 7: SAVINGS CALCULATOR ============
const SavingsCalculatorSection = () => {
  const [vehicles, setVehicles] = useState(5);
  const [currentPremium, setCurrentPremium] = useState(60000);
  const [monthlyTrips, setMonthlyTrips] = useState(500);
  const [monthlyMiles, setMonthlyMiles] = useState(15000);

  const calculations = useMemo(() => {
    // Calculate potential savings based on activity-based model
    // This shows improved cash flow and fleet expansion capacity without revealing per-mile rates
    const traditionalCostPerVehicle = currentPremium / vehicles;
    const utilizationRate = (monthlyTrips * 12) / (vehicles * 365) * 100; // Trips per vehicle per year
    
    // Estimate savings based on typical utilization patterns
    // Lower utilization = higher savings with activity-based model
    let savingsRate;
    if (utilizationRate < 40) savingsRate = 0.45; // 45% savings for low utilization
    else if (utilizationRate < 60) savingsRate = 0.35; // 35% savings for medium utilization
    else if (utilizationRate < 80) savingsRate = 0.25; // 25% savings for good utilization
    else savingsRate = 0.15; // 15% savings for high utilization

    const estimatedSavings = Math.round(currentPremium * savingsRate);
    const improvedCashFlow = Math.round(currentPremium * 0.25); // No large down payment
    const fleetExpansionCapacity = Math.floor(estimatedSavings / (traditionalCostPerVehicle * 0.5)); // Additional vehicles possible

    return {
      estimatedSavings,
      improvedCashFlow,
      fleetExpansionCapacity: Math.max(1, fleetExpansionCapacity),
      savingsPercentage: Math.round(savingsRate * 100)
    };
  }, [vehicles, currentPremium, monthlyTrips, monthlyMiles]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section className="py-24 bg-slate-900" id="calculator">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Savings <span className="text-cyan-400">Calculator</span>
          </h2>
          <p className="text-slate-400">See how activity-based insurance could benefit your operation.</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700"
          >
            <h3 className="text-xl font-bold text-white mb-8">Your Fleet Details</h3>
            
            {/* Number of Vehicles */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-slate-300">Number of Vehicles</label>
                <span className="text-2xl font-bold text-cyan-400">{vehicles}</span>
              </div>
              <Slider
                value={[vehicles]}
                onValueChange={(v) => setVehicles(v[0])}
                min={1}
                max={50}
                step={1}
              />
            </div>

            {/* Current Premium */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-slate-300">Current Annual Insurance Premium</label>
                <span className="text-2xl font-bold text-cyan-400">{formatCurrency(currentPremium)}</span>
              </div>
              <Slider
                value={[currentPremium]}
                onValueChange={(v) => setCurrentPremium(v[0])}
                min={10000}
                max={200000}
                step={5000}
              />
            </div>

            {/* Monthly Trips */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-slate-300">Average Monthly Trips</label>
                <span className="text-2xl font-bold text-cyan-400">{monthlyTrips.toLocaleString()}</span>
              </div>
              <Slider
                value={[monthlyTrips]}
                onValueChange={(v) => setMonthlyTrips(v[0])}
                min={100}
                max={2000}
                step={50}
              />
            </div>

            {/* Monthly Miles */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-3">
                <label className="text-slate-300">Average Monthly Miles</label>
                <span className="text-2xl font-bold text-cyan-400">{monthlyMiles.toLocaleString()}</span>
              </div>
              <Slider
                value={[monthlyMiles]}
                onValueChange={(v) => setMonthlyMiles(v[0])}
                min={5000}
                max={100000}
                step={1000}
              />
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30">
              <p className="text-emerald-400 text-sm mb-2">Estimated Annual Savings</p>
              <p className="text-4xl font-bold text-white">{formatCurrency(calculations.estimatedSavings)}</p>
              <p className="text-emerald-400 text-sm mt-2">Up to {calculations.savingsPercentage}% reduction</p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
              <p className="text-slate-400 text-sm mb-2">Improved Cash Flow</p>
              <p className="text-3xl font-bold text-white">{formatCurrency(calculations.improvedCashFlow)}</p>
              <p className="text-slate-500 text-sm mt-2">No large down payment required</p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
              <p className="text-slate-400 text-sm mb-2">Potential Fleet Expansion Capacity</p>
              <p className="text-3xl font-bold text-white">+{calculations.fleetExpansionCapacity} vehicles</p>
              <p className="text-slate-500 text-sm mt-2">With savings reinvested</p>
            </div>

            <button
              onClick={() => document.getElementById('apply-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full py-4 bg-gradient-to-r from-cyan-500 to-cyan-400 text-slate-900 font-bold rounded-xl hover:shadow-[0_0_30px_rgba(0,212,255,0.3)] transition-all"
            >
              Apply For Custom Quote
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============ SECTION 8: FAQ ============
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: 'Is this a replacement for my state-required insurance?',
      a: 'No. Participating providers must maintain legally required vehicle insurance coverage.'
    },
    {
      q: 'How is mileage tracked?',
      a: 'Mileage is verified through approved trip management and dispatch systems.'
    },
    {
      q: 'Can I add vehicles at any time?',
      a: 'Yes. Qualified vehicles can be added through the onboarding process.'
    },
    {
      q: 'Is there a long-term contract?',
      a: 'Program terms vary by market and provider profile.'
    },
    {
      q: 'How do claims work?',
      a: 'Claims are managed through established reporting and review procedures.'
    }
  ];

  return (
    <section className="py-24 bg-slate-950">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked <span className="text-cyan-400">Questions</span>
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="rounded-xl border border-slate-800 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full p-6 flex items-center justify-between bg-slate-900/50 hover:bg-slate-800/50 transition-colors text-left"
              >
                <span className="text-white font-medium pr-4">{faq.q}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="p-6 pt-0 bg-slate-900/50">
                  <p className="text-slate-400">{faq.a}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ SECTION 9: APPLICATION FORM ============
const ApplicationFormSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    state: '',
    fleetSize: '',
    monthlyTripVolume: '',
    currentInsurancePremium: '',
    currentDispatchSoftware: '',
    additionalNotes: ''
  });

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.email || !formData.state || !formData.fleetSize) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await fetch(`${API_URL}/api/insurance/founding-provider-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.contactName.split(' ')[0] || formData.contactName,
          lastName: formData.contactName.split(' ').slice(1).join(' ') || '',
          email: formData.email,
          phone: formData.phone,
          companyName: formData.companyName,
          state: formData.state,
          city: '',
          fleetSize: formData.fleetSize,
          currentInsurer: '',
          currentPremium: formData.currentInsurancePremium,
          comments: `Monthly Trip Volume: ${formData.monthlyTripVolume}\nDispatch Software: ${formData.currentDispatchSoftware}\n\n${formData.additionalNotes}`,
          source: 'founding_provider_landing'
        })
      });

      setIsSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      setIsSubmitted(true);
      toast.success('Application submitted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500";

  if (isSubmitted) {
    return (
      <section className="py-24 bg-slate-900" id="apply-section">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-12 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Application Received</h3>
            <p className="text-slate-400 mb-6">
              Thank you for your interest in becoming a Founding Provider. 
              Our team will review your application and contact you within 24-48 hours to discuss your custom pricing.
            </p>
            <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-700 inline-flex items-center gap-3">
              <Lock className="w-5 h-5 text-cyan-400" />
              <span className="text-slate-300 text-sm">
                Your market position is being reserved.
              </span>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-slate-900" id="apply-section">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Reserve Your <span className="text-cyan-400">Market Position</span>
          </h2>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Company Name *</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your NEMT Company"
                className={inputClasses}
                required
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Contact Name *</label>
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                placeholder="John Smith"
                className={inputClasses}
                required
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                className={inputClasses}
                required
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">State *</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`${inputClasses} appearance-none`}
                required
              >
                <option value="">Select State</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Fleet Size *</label>
              <select
                name="fleetSize"
                value={formData.fleetSize}
                onChange={handleChange}
                className={`${inputClasses} appearance-none`}
                required
              >
                <option value="">Select Fleet Size</option>
                <option value="1-5">1-5 vehicles</option>
                <option value="6-15">6-15 vehicles</option>
                <option value="16-30">16-30 vehicles</option>
                <option value="31-50">31-50 vehicles</option>
                <option value="50+">50+ vehicles</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Monthly Trip Volume</label>
              <input
                type="text"
                name="monthlyTripVolume"
                value={formData.monthlyTripVolume}
                onChange={handleChange}
                placeholder="e.g., 500 trips"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Current Insurance Premium</label>
              <input
                type="text"
                name="currentInsurancePremium"
                value={formData.currentInsurancePremium}
                onChange={handleChange}
                placeholder="e.g., $60,000/year"
                className={inputClasses}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-slate-400 text-sm mb-2">Current Dispatch Software</label>
              <input
                type="text"
                name="currentDispatchSoftware"
                value={formData.currentDispatchSoftware}
                onChange={handleChange}
                placeholder="e.g., TripMaster, RouteGenie, etc."
                className={inputClasses}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-slate-400 text-sm mb-2">Additional Notes</label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleChange}
                placeholder="Any additional information about your operation..."
                rows={3}
                className={`${inputClasses} resize-none`}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-400 text-slate-900 font-bold text-lg rounded-xl hover:shadow-[0_0_30px_rgba(0,212,255,0.3)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Apply For Founding Provider Status
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

// ============ MAIN COMPONENT ============
export const InsuranceLandingPage = () => {
  return (
    <div className="min-h-screen bg-slate-950" data-testid="insurance-landing-page">
      <HeroSection />
      <WhyBrokenSection />
      <HowItWorksSection />
      <WhyModelWorksSection />
      <WhatMakesDifferentSection />
      <CoverageBenefitsSection />
      <FoundingProviderSection />
      <SavingsCalculatorSection />
      <FAQSection />
      <ApplicationFormSection />
    </div>
  );
};

export default InsuranceLandingPage;
